import { CropYear, FieldViewModel, IOmniaShell, isDate, ISoilAnalysis, numericTextSort, OmniaObjectType, selectMany, SoilAnalysisType, SoilP2O5Method } from "../../omnia"
import { ISoilService } from "../../types"
import { getActivity } from "../activity"
import { AnalysisEditCtrl } from "../edit"
import { getUpdatedComments } from "./edit"
import { getAttributeGroups } from "./groups"
import { AttributeGroup, EditorAttribute } from "./types"

export interface ISoilAllocTarget {
    field: FieldViewModel
    zoneId?: number
}

export class SsmAnalysisEditCtrl extends AnalysisEditCtrl<ISoilAnalysis> {

    static createPopup(): ng.IDirective {
        return super.create(SsmAnalysisEditCtrl)
    }

    private _analysis: ISoilAnalysis

    public userDefinedYear: boolean

    public currentTab: AttributeGroup
    public groups: AttributeGroup[]
    public attrs: EditorAttribute[]

    public selectedSoilAllocTargets: ISoilAllocTarget[]
    public deletedSoilAllocTargets: ISoilAllocTarget[]

    static $inject = ["$scope", "OmniaSoilService", "OmniaShell"]

    constructor($scope: ng.IScope, svc: ISoilService, shell: IOmniaShell) {

        super($scope, svc, shell)

        this.hideLaboratory = true
        this.showFieldRefInId = true

        super.setup()

    }
    protected getTypeInfo() {
        return { name: "SSM analysis", title: "SSM analysis", article: "an", type: OmniaObjectType.SoilAnalysis }
    }

    protected getPopupTemplate(): string {
        return "app/soil/edit/ssm/ssm.html"
    }

    get ssmAttributes() {
        return this.currentTab?.attributes
    }

    protected setupAnalysis(a: ISoilAnalysis) {

        this._analysis = a

        this.allocTargets = this.svc.fields
        this.allocTargets = numericTextSort(this.allocTargets, t => t.description)

        var allocations = !!this._analysis && this._analysis.Allocations ?
            this._analysis.Allocations.filter(a => (a.ZoneAlloc && !!a.ZoneId) || (!a.ZoneAlloc && !a.ZoneId)) :
            []

        this.selectedSoilAllocTargets = !!this._analysis && allocations.length
            ? allocations.map(a => <ISoilAllocTarget>{
                field: this.allocTargets.find(f => f.fieldId === a.FieldId),
                zoneId: a.ZoneId
            })
            : this.cmd && this.cmd.alloc ? this.allocTargets.filter(f => f.fieldId === this.cmd.alloc.fieldId).map(f => <ISoilAllocTarget>{ field: f }) : []

        this.deletedSoilAllocTargets = []

        a.P2O5Method = a.P2O5Method || SoilP2O5Method.Olsen

        this.userDefinedYear = !this.newAnalysis

        if (this.newAnalysis) {

            a.Type = SoilAnalysisType.SSM

            if ((!a.FieldReference) && this.selectedSoilAllocTargets.length) {
                a.FieldReference = this.selectedSoilAllocTargets[0].field.fieldName
            }

        }

        this.groups = getAttributeGroups(this.svc.getSsmClassification(), this.analysis)
        this.attrs = selectMany(this.groups.map(g => g.attributes))
        this.currentTab = this.groups[0]

    }

    protected getActivity() {
        const a = this._analysis
        return getActivity(this.newAnalysis, { type: OmniaObjectType.SoilAnalysis, id: a?.SoilAnalysisId, number: a?.AnalysisNumber })
    }

    selectField(field: FieldViewModel) {
        if (field) {
            this.currentAllocSelection = null

            if (!this.selectedSoilAllocTargets.find(a => a.field.fieldId === field.fieldId && !a.zoneId) &&
                !this.deletedSoilAllocTargets.find(a => a.field.fieldId === field.fieldId && !a.zoneId)) {
                this.selectedSoilAllocTargets.push({ field: field })
            }
        }

        if (this.newAnalysis && field && (!this._analysis.FieldReference)) {
            this._analysis.FieldReference = field.fieldName
        }
    }

    unselectTarget(field: ISoilAllocTarget) {
        if (field && this.selectedSoilAllocTargets.indexOf(field) !== -1) {

            if (this.analysis.Allocations && this.analysis.Allocations.filter(a => a.FieldId === field.field.fieldId && !a.ZoneAlloc).length > 0) {
                if (this.deletedSoilAllocTargets.indexOf(field) === -1) {
                    this.deletedSoilAllocTargets.push(field)
                }
            }

            this.selectedSoilAllocTargets = this.selectedSoilAllocTargets.filter(f => field !== f)
        }
    }

    recoverTarget(field: ISoilAllocTarget) {
        if (field && this.deletedSoilAllocTargets.indexOf(field) !== -1) {
            this.deletedSoilAllocTargets = this.deletedSoilAllocTargets.filter(f => field !== f)

            if (this.selectedSoilAllocTargets.indexOf(field) === -1) {
                this.selectedSoilAllocTargets.push(field)
            }
        }
    }

    protected validate() {
        return this.attrs?.every(a => !a.error)
    }

    protected updateOnDateChanged() {

        let currentYear = this._analysis.FirstYear
        if (this.userDefinedYear && currentYear) return

        let date = this.analysis.SamplingDate

        if (isDate(date)) {
            this._analysis.FirstYear = CropYear.getYearFromDate(date)
        }

    }

    protected saveAnalysis(): ng.IPromise<number> {

        this._analysis.IsValid = true

        let allocFieldsIds = this.selectedSoilAllocTargets && this.selectedSoilAllocTargets.length > 0
            ? this.selectedSoilAllocTargets.filter(a => !a.zoneId).map(f => f.field.fieldId)
            : null

        if (this._analysis.CO2Burst) {
            this._analysis.CO2Burst = Math.round(this._analysis.CO2Burst * 100) / 100
        }

        const fieldIds = this.newAnalysis && this.allocTarget
            ? [this.allocTarget.fieldId]
            : allocFieldsIds

        const comments = getUpdatedComments(this._analysis, this.attrs)

        const analysis = {
            ...this._analysis,
            Interpretations: undefined
        }
        return this.svc
            .saveSoilAnalysis(analysis, fieldIds, comments)
            .then(a => a ? a.SoilAnalysisId : null)

    }

    protected deleteAnalysis(deleteOtherGridSamples: boolean) {

        let id = this._analysis.SoilAnalysisId
        if (!id) return null

        return this.svc.deleteSoilAnalysis(id, deleteOtherGridSamples)
    }

}
